import React, { useMemo, useState } from "react";
import {
  Box,
  MenuItem,
  Button,
  Stack,
  Typography,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import FilterListIcon from "@mui/icons-material/FilterList";
import CalendarTodayIcon from "@mui/icons-material/CalendarTodayOutlined";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import FilterChip from "../../components/FilterChip";

export interface ContactFilters {
  status?: string;
  contacted?: string;
  country?: string;
  from?: string;
  to?: string;
}

const STATUS = ["NEW", "CONTACTED", "RESCHEDULED", "QUALIFIED", "WON", "LOST", "SPAM"];

const ContactRequestFilters = ({
  onApply,
}: {
  onApply: (filters: ContactFilters) => void;
}) => {
  const [filters, setFilters] = useState<ContactFilters>({});

  const activeStatusLabel = useMemo(() => {
    return filters.status ? `Status: ${filters.status}` : undefined;
  }, [filters.status]);

  const activeDateLabel = useMemo(() => {
    if (!filters.from && !filters.to) return undefined;
    if (filters.from && filters.to) {
      return `${dayjs(filters.from).format("MMM D")} – ${dayjs(filters.to).format("MMM D")}`;
    }
    if (filters.from) return `From: ${dayjs(filters.from).format("MMM D")}`;
    return `To: ${dayjs(filters.to).format("MMM D")}`;
  }, [filters.from, filters.to]);

  const hasActiveFilters = Object.values(filters).some(Boolean);

  const handleStatusChange = (val: string) => {
    const nextFilters = { ...filters, status: val || undefined };
    setFilters(nextFilters);
    onApply(nextFilters);
  };

  const handleDateApply = (close: () => void) => {
    onApply(filters);
    close();
  };

  const handleReset = () => {
    setFilters({});
    onApply({});
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
        py: 1,
        mb: 2,
      }}
    >
      {/* Filter label */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          color: "text.disabled",
          mr: 0.5,
        }}
      >
        <FilterListIcon sx={{ fontSize: 16 }} />
        <Typography fontSize="12px" fontWeight={600} color="text.disabled">
          Filters
        </Typography>
      </Box>

      {/* Divider */}
      <Box sx={{ width: "1px", height: 20, bgcolor: "#E2E8F0", mr: 0.5 }} />

      {/* Status Filter */}
      <FilterChip
        id="contact-status"
        label="All Statuses"
        activeLabel={activeStatusLabel}
      >
        {(close) => (
          <Box sx={{ p: 1 }}>
            <MenuItem
              selected={!filters.status}
              onClick={() => {
                handleStatusChange("");
                close();
              }}
              sx={{ borderRadius: "8px", fontSize: "13px", mb: 0.25 }}
            >
              All Statuses
            </MenuItem>
            {STATUS.map((s) => (
              <MenuItem
                key={s}
                selected={filters.status === s}
                onClick={() => {
                  handleStatusChange(s);
                  close();
                }}
                sx={{
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: filters.status === s ? 600 : 400,
                  mb: 0.25,
                }}
              >
                {s}
              </MenuItem>
            ))}
          </Box>
        )}
      </FilterChip>

      {/* Date Range Filter */}
      <FilterChip
        id="contact-date"
        label="Date Range"
        activeLabel={activeDateLabel}
        icon={<CalendarTodayIcon />}
      >
        {(close) => (
          <Box sx={{ p: 1.5 }}>
            <Stack spacing={1.5} sx={{ minWidth: 220 }}>
              <DatePicker
                label="From date"
                value={filters.from ? dayjs(filters.from) : null}
                maxDate={filters.to ? dayjs(filters.to) : undefined}
                onChange={(val: Dayjs | null) =>
                  setFilters({
                    ...filters,
                    from: val ? val.format("YYYY-MM-DD") : undefined,
                  })
                }
                renderInput={(params) => (
                  <TextField {...params} size="small" fullWidth />
                )}
              />
              <DatePicker
                label="To date"
                value={filters.to ? dayjs(filters.to) : null}
                minDate={filters.from ? dayjs(filters.from) : undefined}
                onChange={(val: Dayjs | null) =>
                  setFilters({
                    ...filters,
                    to: val ? val.format("YYYY-MM-DD") : undefined,
                  })
                }
                renderInput={(params) => (
                  <TextField {...params} size="small" fullWidth />
                )}
              />
              <Button
                size="small"
                variant="contained"
                onClick={() => handleDateApply(close)}
                sx={{ borderRadius: "8px", fontWeight: 600 }}
              >
                Apply
              </Button>
            </Stack>
          </Box>
        )}
      </FilterChip>

      {/* Clear All */}
      {hasActiveFilters && (
        <>
          <Box sx={{ width: "1px", height: 20, bgcolor: "#E2E8F0", mx: 0.5 }} />
          <Button
            size="small"
            startIcon={<CloseIcon sx={{ fontSize: "14px !important" }} />}
            onClick={handleReset}
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              color: "text.secondary",
              textTransform: "none",
              borderRadius: "8px",
              px: 1.5,
              py: 0.5,
              "&:hover": { color: "error.main", bgcolor: "#FEF2F2" },
            }}
          >
            Clear
          </Button>
        </>
      )}
    </Box>
  );
};

export default ContactRequestFilters;
